// LiveIndicator — unified "Live / Updated X ago" component used across all FORGE pages.
// Replaces the per-file RefreshDot and LiveIndicator variants.
//
// Status colours:
//   green (pulsing) — updated within the last 5 minutes
//   amber           — updated 5–30 minutes ago
//   gray            — no data or older than 30 minutes

import { useState, useEffect } from "react";

export interface LiveIndicatorProps {
  /** The timestamp of the last successful data fetch. Pass null when no data yet. */
  lastUpdatedAt: Date | null;
  /** Whether a fetch is currently in flight. Shows a loading shimmer when true. */
  isLoading?: boolean;
  /** Polling interval in ms — used only to determine staleness thresholds. Optional. */
  pollingInterval?: number;
}

function ageMs(d: Date): number {
  return Date.now() - d.getTime();
}

export function formatAge(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  // Anything past two days used to keep counting in hours ("458h ago" on the
  // frozen aggregate layer) — technically true and unreadable, which is its
  // own kind of dishonesty. Days are what a reader can act on.
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

export function LiveIndicator({
  lastUpdatedAt,
  isLoading = false,
}: LiveIndicatorProps) {
  // Re-render every 15 s so the "X ago" label stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  if (isLoading && !lastUpdatedAt) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-theme-muted animate-pulse select-none">
        <span className="w-1.5 h-1.5 rounded-full bg-theme-muted shrink-0" />
        <span>Loading…</span>
      </div>
    );
  }

  if (!lastUpdatedAt) return null;

  const ms = ageMs(lastUpdatedAt);
  const stale = ms > 30 * 60_000; // >30 min → gray
  const aging = ms > 5 * 60_000; // 5–30 min → amber

  const dotClass = stale
    ? "bg-zinc-400 dark:bg-zinc-600"
    : aging
      ? "bg-amber-400"
      : "bg-status-green";

  const textClass = stale
    ? "text-theme-muted"
    : aging
      ? "text-amber-500 dark:text-amber-400"
      : "text-theme-muted";

  return (
    <div
      className={`flex items-center gap-1.5 text-xs select-none ${textClass}`}
    >
      <span className="relative flex h-2 w-2 shrink-0">
        {!stale && !aging && (
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${dotClass}`}
          />
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${dotClass}`}
        />
      </span>
      {/* "Live" is a claim, not a label. Past the stale threshold the feed is
          by definition not live, and saying so anyway is how a dead source
          reads as healthy. Drop the word and state the age instead. */}
      <span>
        {stale ? (
          <>Last updated {formatAge(ms)}</>
        ) : (
          <>
            Live <span className="opacity-70">· Updated {formatAge(ms)}</span>
          </>
        )}
      </span>
    </div>
  );
}

export default LiveIndicator;
